import React, { useEffect, useState, useRef } from "react";
import Tables from "../../../common/components/DataTable/tables";
import Pagination from "../../../common/components/Pagination/Pagination";
import "./operationHome.css";
import { columnConfig } from "./columnConfig";
import OperationsHome from "../../../common/components/Operation/operation";
import Sidebar from "../../../common/components/Main Menu/MainMenu";
import SideCardComponent from "../../../common/components/SideCard/SideCardComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
  getOperationFilteredDataInit,
  getOperationDataInit,
  setSelectedCard,
  fetchFilteredTableDataInit,
  setSearchFields,
  setStartDate,
  setEndDate,
  setActiveTab,
  fetchWorkScreenData,
} from "../../../redux/slices/operationHomeSlice";
import Breadcrumb from "../../../common/components/NavigationBreadcrumb/Breadcrumb";
import Spinner from "../../../common/components/Spinner/Spinner";
import getIntervalParams from "../../../common/Helper/getIntervalParams";
import { notify } from "../../../common/Helper/notification";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
function Home() {
  const dispatch = useDispatch();
  const location = useLocation();

  const [indexOfFirstItem, setIndexOfFirstItem] = useState(0);
  const [indexOfLastItem, setIndexOfLastItem] = useState(0);
  const [currentData, setCurrentData] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("operationHomeSidebarOpen");
    return savedState ? savedState === "true" : true;
  });

  const tableData = useSelector((state) => state.operationHome.table.data);

  const filteredData = useSelector(
    (state) => state.operationHome.table.filteredData,
  );

  const currentPage = useSelector(
    (state) => state.operationHome.table.currentPage,
  );

  const pageSize = useSelector((state) => state.operationHome.table.pageSize);

  const loading = useSelector((state) => state.operationHome.loading);

  const activeTab = useSelector((state) => state.operationHome.activeTab);

  const startDate = useSelector((state) => state.operationHome.tabStartDate);

  const endDate = useSelector((state) => state.operationHome.tabEndDate);

  const requestError = useSelector((state) => state.operationHome.requestError);

  const totalDataCount = useSelector(
    (state) => state.operationHome.table.totalDataCount,
  );

  const searchFields = useSelector((state) => state.operationHome.searchFields);

  const handleDownloadStarted = (type) => {
    toast.info(`${type} PDF download started...`, {
      position: "top-right",
      autoClose: 3000,
      theme: "colored",
    });
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("operationHomeSidebarOpen", String(next));
      return next;
    });
  };

  useEffect(() => {
    if (requestError && String(requestError).length > 0) {
      notify.error(requestError);
    }
  }, [requestError]);

  useEffect(() => {
    if (
      !loading &&
      activeTab === "range" &&
      startDate &&
      endDate &&
      totalDataCount === 0
    ) {
      notify.info(`No data found for ${startDate} - ${endDate}`);
    }
  }, [loading, totalDataCount]);

  // Pre-fetch workScreenData so workScreenData.assignee is available for the
  // agent filter dropdown in Ths without needing ActionPanel to open first.
  const assigneePrefetchedRef = useRef(false);
  useEffect(() => {
    if (!assigneePrefetchedRef.current && tableData && tableData.length > 0) {
      const firstRow = tableData[0];
      if (firstRow.memberId && firstRow.callId) {
        assigneePrefetchedRef.current = true;
        dispatch(
          fetchWorkScreenData({
            memberId: firstRow.memberId,
            callId: firstRow.callId,
          }),
        );
      }
    }
  }, [tableData]);

  useEffect(() => {
    const comingFromDetails = location.state?.from === "details";

    if (!comingFromDetails) {
      // Fresh navigation (from Landing or direct URL): reset all filters and load default data.
      dispatch(setSearchFields({}));
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
      dispatch(setActiveTab("last24Hours"));
      dispatch(getOperationDataInit({ name: "last24Hours", limit: pageSize }));
      return;
    }

    // Coming back from the Details page: restore previous filters and refetch.
    const hasFilters = searchFields && Object.keys(searchFields).length > 0;

    if (hasFilters) {
      const params = getIntervalParams(activeTab, startDate, endDate);
      params.limit = pageSize;
      dispatch(
        getOperationFilteredDataInit({
          body: { ...searchFields, page: currentPage },
          params,
        }),
      );
    } else {
      dispatch(
        getOperationDataInit({
          name: activeTab,
          startDate,
          endDate,
          limit: pageSize,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lastItem =
      totalDataCount === pageSize
        ? currentPage * pageSize
        : Math.min(currentPage * pageSize, totalDataCount);

    const firstItem = currentPage * pageSize - pageSize;

    setIndexOfLastItem(lastItem);
    setIndexOfFirstItem(firstItem);

    if (filteredData) {
      const displayData = tableData.slice(firstItem, lastItem);
      setCurrentData(displayData);
    }
  }, [filteredData, currentPage, pageSize, totalDataCount, searchFields]);

  const handleSearch = (value) => {
    // Guard against invalid input (null/undefined/non-string) crashing the search.
    const rawTerm = typeof value?.searchTerm === "string" ? value.searchTerm : "";
    const trimmedTerm = rawTerm.trim();
    const client = value?.client || "";
    const severity = value?.severity || "";

    const params = getIntervalParams(activeTab, startDate, endDate);
    params.limit = pageSize;

    dispatch(setSelectedCard(null));

    // Nothing to filter by at all (no text, no employer group, no severity):
    // reset the table to the default dataset.
    if (!trimmedTerm && !client && !severity) {
      dispatch(setSearchFields({}));
      dispatch(setCurrentPage(1));
      dispatch(
        getOperationDataInit({
          name: activeTab,
          startDate,
          endDate,
          limit: pageSize,
        }),
      );
      return;
    }

    // Prevent duplicate/overlapping requests while one is already in flight.
    if (loading) return;

    // The backend only matches call status against the full value ("open"
    // matches, "op" doesn't), unlike names which get partial matching. So if
    // the term is an unambiguous prefix of a known status (or of one of its
    // words, e.g. "prog" → "in-progress"), expand it to the full status
    // before sending.
    const CALL_STATUSES = ["open", "closed", "in-progress"];
    // Compare with spaces/hyphens stripped so "in progress", "inprogress"
    // and "in-prog" all resolve to "in-progress".
    const normalizedTerm = trimmedTerm.toLowerCase().replace(/[\s-]+/g, "");
    const statusMatches = normalizedTerm
      ? CALL_STATUSES.filter(
          (s) =>
            s.replace(/-/g, "").startsWith(normalizedTerm) ||
            s.split("-").some((word) => word.startsWith(normalizedTerm)),
        )
      : [];
    const effectiveTerm =
      statusMatches.length === 1 ? statusMatches[0] : trimmedTerm;

    // Smart search: an ID is alphanumeric and contains at least one digit
    // (e.g. MH ID/Vonage/Member/Call IDs); anything else is treated as an
    // agent or member name. The backend performs case-insensitive partial
    // matching against whichever field applies.
    const isIdLike =
      !!effectiveTerm &&
      /^[a-zA-Z0-9-]+$/.test(effectiveTerm) &&
      /\d/.test(effectiveTerm);

    const body = {
      search: "advanceSearch",
      searchText: effectiveTerm,
      ...(trimmedTerm ? { searchType: isIdLike ? "id" : "name" } : {}),
      client,
      severity,
      page: 1,
    };

    dispatch(getOperationFilteredDataInit({ body, params }));
  };

  const paginateHandler = (pageNumber) => {
    dispatch(setCurrentPage(pageNumber));

    const params = getIntervalParams(activeTab, startDate, endDate);
    params.limit = pageSize;

    const hasFilters = searchFields && Object.keys(searchFields).length > 0;

    if (hasFilters) {
      let body;
      let allSearches;
      if (searchFields.searches) {
        // Column-level filter format — send all filters including qualityOfCall to the API
        const apiSearches = searchFields.searches;
        body = { searches: apiSearches, page: pageNumber };
        allSearches = searchFields.searches;
      } else {
        // Advanced filter format
        body = { ...searchFields, page: pageNumber };
      }
      dispatch(getOperationFilteredDataInit({ body, allSearches, params }));
    } else {
      dispatch(
        fetchFilteredTableDataInit({
          pageSize,
          currentPage: pageNumber,
          activeTab,
          startDate,
          endDate,
          searchFields,
        }),
      );
    }
  };

  const pageSizeHandler = (size) => {
    dispatch(setPageSize(size));
    dispatch(setCurrentPage(1));

    const params = getIntervalParams(activeTab, startDate, endDate);
    params.limit = size;

    const hasFilters = searchFields && Object.keys(searchFields).length > 0;

    if (hasFilters) {
      let body;
      let allSearches;
      if (searchFields.searches) {
        body = { searches: searchFields.searches, page: 1 };
        allSearches = searchFields.searches;
      } else {
        body = { ...searchFields, page: 1 };
      }
      dispatch(getOperationFilteredDataInit({ body, allSearches, params }));
    } else {
      dispatch(
        fetchFilteredTableDataInit({
          pageSize: size,
          currentPage: 1,
          activeTab,
          startDate,
          endDate,
          searchFields,
        }),
      );
    }
  };

  const breadcrumbItems = [
    { label: "Home", link: "/landing" },
    { label: "Ops Portal", link: "/operationHome" },
  ];

  console.log("filteredData ===>", filteredData);

  return (
    <>
      {loading && <Spinner loading={loading} />}

      <div className="main">
        <Navbar />

        <div className="App">
          {/* <Sidebar logoLink="/landing" /> */}

          <div className="main-container">
            <div className=" ms-3">
              {" "}
              {/* ms-2 adds a small left margin, ms-3 adds more */}
              <Breadcrumb
                breadcrumbItems={breadcrumbItems}
                backLink="/operationHome"
                backLinkState={{ from: "details" }}
              />
            </div>

            <div className="secondary-container">
              <div
                className={`third-container ${isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"}`}
              >
                <OperationsHome onSearch={handleSearch} />

                <div className="operation-table-area">
                  <Tables data={filteredData} columnConfig={columnConfig} />
                </div>

                <div className="operation-pagination-area">
                  <Pagination
                    totalPage={
                      totalDataCount > 0
                        ? Math.ceil(totalDataCount / pageSize)
                        : 0
                    }
                    currentPage={currentPage}
                    limit={pageSize}
                    paginateHandler={paginateHandler}
                    setPageSize={pageSizeHandler}
                    totalData={totalDataCount}
                    tableStartIndex={indexOfFirstItem + 1}
                    tableEndIndex={indexOfLastItem}
                  />
                </div>
              </div>

              <button
                type="button"
                className={`sidebar-toggle-button ${isSidebarOpen ? "expanded" : "collapsed"}`}
                onClick={toggleSidebar}
                title={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                aria-label={
                  isSidebarOpen
                    ? "Collapse right sidebar"
                    : "Expand right sidebar"
                }
                aria-expanded={isSidebarOpen}
                aria-controls="operation-right-sidebar"
              >
                {isSidebarOpen ? <FiChevronRight /> : <FiChevronLeft />}
              </button>

              <div
                id="operation-right-sidebar"
                className={`right-sidebar-panel ${isSidebarOpen ? "open" : "closed"}`}
              >
                <div className="right-sidebar-content">
                  <SideCardComponent
                    onDownloadStarted={handleDownloadStarted}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
